import {Button, Input, Select, SelectItem, Textarea} from "@heroui/react";
import {addToast} from "@heroui/toast";
import {createFileRoute} from "@tanstack/react-router";
import React, {useState} from "react";
import {config} from "../config/config.ts";
import FileUploader from "../components/FileUploader.tsx";
import GoogleMapPicker from "../components/GoogleMapPicker.tsx";
import {GeoPoint} from "../types";
import LandingNav from "../landing/components/LandingNav.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";
import services from "../services";
import {sportsForSurface, useSports} from "../sports/registry.ts";

export const Route = createFileRoute('/list-club')({
    component: RouteComponent,
})

// The listing form's sports, from the sport registry's 'listing' surface. The form sends the stored
// key (so 'pickelball' — the backend normalises either spelling and listings are stored under it).
// Read at call time, never at module load (see quiz.tsx).
const sportOptions = () => sportsForSurface('listing').map((entry) => ({key: entry.key, label: entry.label}));

function RouteComponent() {
    useSports(); // re-render with the full sport list once the live registry lands
    const [form, setForm] = useState({
        name: '', description: '', website: '', phone: '', email: '',
        address: '', city: '', country: '',
        image_url: '', logo_url: '',
        contact_name: '', contact_email: '', contact_phone: '',
    });
    const [sports, setSports] = useState<string[]>([]);
    const [location, setLocation] = useState<GeoPoint | undefined>(undefined);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const set = (k: string, v: string) => setForm((f) => ({...f, [k]: v}));

    const submit = async () => {
        if (!form.name.trim() || !form.contact_name.trim() || !form.contact_email.trim()) {
            addToast({title: 'Please add the club name, your name and your email.', color: 'danger'});
            return;
        }
        setSubmitting(true);
        try {
            const [lng, lat] = location?.coordinates || [];
            const res = await services.clubDirectory.create({
                name:          form.name,
                sport_types:   sports.join(','),
                description:   form.description,
                image_url:     form.image_url,
                logo_url:      form.logo_url,
                address:       form.address,
                city:          form.city,
                country:       form.country,
                lat:           lat ?? null,
                lng:           lng ?? null,
                phone:         form.phone,
                email:         form.email,
                website:       form.website,
                contact_name:  form.contact_name,
                contact_email: form.contact_email,
                contact_phone: form.contact_phone,
            });
            if (res?.error) {
                addToast({title: res.error, color: 'danger'});
                setSubmitting(false);
                return;
            }
            setDone(true);
        } catch (e) {
            addToast({title: 'Something went wrong. Please try again.', color: 'danger'});
        }
        setSubmitting(false);
    };

    return (
        <main className="min-h-screen bg-[#EDEEF4] font-inter text-[#071a5f]">
            <title>List Your Sports Club | ScorePal</title>
            <meta name="description" content="Add your sports club to the ScorePal directory so players can discover it." />
            <link rel="canonical" href={`${config.publicUrl}/list-club/`} />

            <section className="relative overflow-hidden p-4">
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20"><LandingNav /></div>
                    <div className="container mx-auto px-6 pt-8 lg:px-12">
                        <div className="max-w-3xl">
                            <h1 className="mb-3 font-bebas text-[clamp(36px,5vw,60px)] font-bold uppercase leading-none tracking-wide text-white">
                                List your club
                            </h1>
                            <p className="max-w-2xl font-inter text-base leading-relaxed text-white/85 lg:text-lg">
                                Add your sports club to the ScorePal directory so players in your area can find you.
                                Submissions are reviewed before they go live.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <div className="container mx-auto max-w-2xl px-4 pb-20 lg:px-12">
                <div className="-mt-6 rounded-3xl border border-[#dfe6f5] bg-white p-6 shadow-[0_18px_45px_rgba(8,47,138,0.12)] sm:p-8">
                    {done ? (
                        <div className="py-10 text-center">
                            <h2 className="font-bebas text-3xl text-[#06195f]">Thanks — we've got it!</h2>
                            <p className="mt-2 text-[#5770ab]">
                                Your club has been submitted for listing. We'll review it and add it to the directory shortly.
                            </p>
                        </div>
                    ) : (
                        <div className="grid gap-4">
                            <h2 className="font-bebas text-2xl text-[#06195f]">Club details</h2>
                            <Input isRequired label="Club name" value={form.name} onValueChange={(v) => set('name', v)} />
                            <Select label="Sports" selectionMode="multiple"
                                    selectedKeys={new Set(sports)}
                                    onSelectionChange={(keys) => setSports(Array.from(keys as Set<string>))}>
                                {sportOptions().map((s) => <SelectItem key={s.key}>{s.label}</SelectItem>)}
                            </Select>
                            <Textarea label="About the club" minRows={3} value={form.description} onValueChange={(v) => set('description', v)} />

                            <div className="grid gap-4 sm:grid-cols-2">
                                <ImageField label="Logo (optional)" value={form.logo_url} category="logos" onUploaded={(url) => set('logo_url', url)} />
                                <ImageField label="Photo (optional)" value={form.image_url} category="posts" onUploaded={(url) => set('image_url', url)} />
                            </div>

                            <h2 className="mt-2 font-bebas text-2xl text-[#06195f]">Location</h2>
                            <Input label="Address" value={form.address} onValueChange={(v) => set('address', v)} />
                            <div className="grid gap-4 sm:grid-cols-2">
                                <Input label="City" value={form.city} onValueChange={(v) => set('city', v)} />
                                <Input label="Country" value={form.country} onValueChange={(v) => set('country', v)} />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm text-[#5770ab]">Location on the map — auto-fills from the address above; tap the map or use your location to fine-tune.</label>
                                <GoogleMapPicker
                                    value={location}
                                    onChange={setLocation}
                                    geocodeQuery={[form.address, form.city, form.country].filter(Boolean).join(', ')}
                                    enableMyLocation
                                />
                            </div>

                            <h2 className="mt-2 font-bebas text-2xl text-[#06195f]">Contact &amp; links</h2>
                            <Input label="Website" type="url" placeholder="https://" value={form.website} onValueChange={(v) => set('website', v)} />
                            <div className="grid gap-4 sm:grid-cols-2">
                                <Input label="Public phone (optional)" value={form.phone} onValueChange={(v) => set('phone', v)} />
                                <Input label="Public email (optional)" type="email" value={form.email} onValueChange={(v) => set('email', v)} />
                            </div>

                            <h2 className="mt-2 font-bebas text-2xl text-[#06195f]">Your contact</h2>
                            <Input isRequired label="Your name" value={form.contact_name} onValueChange={(v) => set('contact_name', v)} />
                            <Input isRequired label="Email" type="email" value={form.contact_email} onValueChange={(v) => set('contact_email', v)} />
                            <Input label="Phone (optional)" value={form.contact_phone} onValueChange={(v) => set('contact_phone', v)} />

                            <Button color="primary" size="lg" isLoading={submitting} onPress={submit} className="mt-2">
                                Submit club
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <FooterSection />
        </main>
    );
}

function ImageField({label, value, category, onUploaded}: {
    label: string; value: string; category: 'posts' | 'logos'; onUploaded: (url: string) => void;
}) {
    return (
        <div>
            <label className="mb-1 block text-sm text-[#5770ab]">{label}</label>
            <div className="flex items-center gap-3">
                {value && <img src={value} alt="" className="h-16 w-16 shrink-0 rounded-lg object-cover" />}
                <FileUploader
                    className="flex-1"
                    category={category}
                    buttonText={value ? 'Change' : 'Upload'}
                    onUploaded={(data) => onUploaded(data.image_url)}
                />
            </div>
        </div>
    );
}
